import DeviceConfig from '@devices/config';
import { Keys } from '@devices/keys';
import { store } from '../../../store/store';
import type Focus from '../../../types/enums/focus';
import { IKeyboard } from '../../../types/interfaces/keyboard';
import { useEffect } from 'react';

const PRESSED_KEYS = 1;
const PRESS_DELAY = 1000;

/** Focus value at the start of the current keydown event (set in capture phase). */
let focusAtKeyDownStart: number | number[] | (number | string)[] | null = null;

/** One entry per mounted `useKeyboard()` call, dispatched to by the single set of document listeners below. */
type Subscriber = {
  focus: Focus | number | Focus[] | number[] | undefined;
  canBeFocused: boolean;
  stopPropagation: boolean;
  keyDownHandler?: IKeyboard['keyDownHandler'];
  keyUpHandler?: IKeyboard['keyUpHandler'];
  keyPressHandler?: IKeyboard['keyPressHandler'];
  pointerUpHandler?: IKeyboard['pointerUpHandler'];
  keyDownDelay?: number;
  lastKeyDownTime: number;
  lastKey: string;
  pressed: number;
  lastFocus: number;
  keyTimeout?: ReturnType<typeof setTimeout>;
};

/** Insertion-ordered, mirrors the old addEventListener registration order used for `stopPropagation` early-exit. */
const subscribers: Subscriber[] = [];

function wasFocusedAtKeyDownStart(
  focus: Focus | number | Focus[] | number[] | undefined
): boolean {
  if (focusAtKeyDownStart === null) return false;
  if (focus === undefined) return false;
  if (focusAtKeyDownStart === focus) return true;
  const focusNum = typeof focus === 'number' ? focus : undefined;
  const startNum =
    typeof focusAtKeyDownStart === 'number' ? focusAtKeyDownStart : undefined;
  if (
    Array.isArray(focusAtKeyDownStart) &&
    focusNum !== undefined &&
    (focusAtKeyDownStart as (number | string)[]).includes(focusNum)
  )
    return true;
  if (
    Array.isArray(focus) &&
    startNum !== undefined &&
    (focus as number[]).includes(startNum)
  )
    return true;
  if (
    Array.isArray(focus) &&
    Array.isArray(focusAtKeyDownStart) &&
    (focusAtKeyDownStart as (number | string)[]).some((v) =>
      (focus as number[]).includes(Number(v))
    )
  )
    return true;
  return false;
}

function isFocused(sub: Subscriber): boolean {
  const { focus: state } = store.getState();
  if (state.focus === null) return false;
  return (
    state.focus === sub.focus ||
    ((state.focus as number[])?.length &&
      (state.focus as number[]).includes(sub.focus as number)) ||
    (Array.isArray(sub.focus) &&
      (sub.focus as number[]).includes(state.focus as number))
  );
}

const keyDownCapture = () => {
  const slice = store.getState().focus;
  focusAtKeyDownStart =
    slice?.focus !== undefined && slice?.focus !== null ? slice.focus : null;
};

const dispatchKeyDown = (event: KeyboardEvent) => {
  for (const sub of subscribers) {
    if (!sub.keyDownHandler) continue;
    const shouldHandle =
      (!sub.canBeFocused || isFocused(sub)) &&
      (sub.canBeFocused ? wasFocusedAtKeyDownStart(sub.focus) : true);
    if (!shouldHandle) continue;

    const parsedKey =
      Keys[event.key] || Keys[event.code] || Keys[event.keyCode];
    sub.pressed = sub.lastKey === parsedKey ? sub.pressed + 1 : 0;
    sub.lastKey = parsedKey;
    const now = Date.now();
    if (
      !event.repeat ||
      !sub.keyDownDelay ||
      now - sub.lastKeyDownTime >= sub.keyDownDelay
    ) {
      sub.lastKeyDownTime = now;
      sub.keyDownHandler(parsedKey, sub.pressed > PRESSED_KEYS, sub.pressed);
    }
    clearTimeout(sub.keyTimeout);
    if (DeviceConfig.capabilities.repeatSelect) {
      sub.lastFocus = Array.isArray(sub.focus)
        ? sub.focus[0] ?? -1
        : sub.focus ?? -1;
    }
    if (parsedKey === 'SELECT' && sub.keyPressHandler) {
      if (!DeviceConfig.capabilities.repeatSelect) {
        sub.keyTimeout = setTimeout(() => {
          sub.keyPressHandler?.();
        }, PRESS_DELAY);
      } else if (sub.pressed > PRESSED_KEYS) {
        sub.keyPressHandler();
      }
    }
    if (sub.stopPropagation) {
      event.preventDefault();
      break;
    }
  }
};

const dispatchKeyUp = (event: KeyboardEvent) => {
  for (const sub of subscribers) {
    clearTimeout(sub.keyTimeout);
    let stop = false;
    if (sub.keyUpHandler && (!sub.canBeFocused || isFocused(sub))) {
      const pressedKey =
        (event.key !== 'Unidentified' ? event.key : null) ||
        event.code ||
        event.keyCode;
      const parsedKey = Keys[pressedKey];
      if (
        sub.lastKey === parsedKey ||
        (parsedKey === 'SELECT' &&
          !DeviceConfig.capabilities.repeatSelect &&
          sub.lastFocus === sub.focus)
      ) {
        sub.keyUpHandler(parsedKey, sub.pressed > PRESSED_KEYS, sub.pressed);
      }
      if (sub.stopPropagation) {
        event.preventDefault();
        stop = true;
      }
    }
    sub.pressed = 0;
    if (stop) break;
  }
};

const dispatchPointerUp = () => {
  const { focus: state } = store.getState();
  for (const sub of subscribers) {
    if (
      sub.pointerUpHandler &&
      (state.focus === sub.focus || !sub.canBeFocused)
    ) {
      sub.pointerUpHandler();
    }
    sub.pressed = 0;
  }
};

const dispatchWheel = (event: Event) => {
  const wheelEvent = event as WheelEvent;
  const parsedKey = (wheelEvent?.deltaY || 0) > 0 ? 'DOWN' : 'UP';
  for (const sub of subscribers) {
    if (!sub.canBeFocused || isFocused(sub)) {
      sub.pressed = sub.lastKey === parsedKey ? sub.pressed + 1 : 0;
      sub.lastKey = parsedKey;
      const now = Date.now();
      if (!sub.keyDownDelay || now - sub.lastKeyDownTime >= sub.keyDownDelay) {
        sub.lastKeyDownTime = now;
        sub.keyDownHandler?.(
          parsedKey,
          sub.pressed > PRESSED_KEYS,
          sub.pressed
        );
      }
      if (sub.stopPropagation) break;
    }
  }
};

// Attached once for the app's lifetime - `useKeyboard()` calls only register/unregister
// subscribers above, they never touch the DOM directly. Guarded on `window` (rather than a
// module-scoped flag) because dev-mode hot reloading of this file re-executes this module.
declare global {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Window {
    __useKeyboardListenersAttached?: boolean;
  }
}
if (typeof document !== 'undefined' && !window.__useKeyboardListenersAttached) {
  window.__useKeyboardListenersAttached = true;
  document.addEventListener('keydown', keyDownCapture, true);
  document.addEventListener('keydown', dispatchKeyDown);
  document.addEventListener('keyup', dispatchKeyUp);
  document.addEventListener('pointerup', dispatchPointerUp);
  document.addEventListener('wheel', dispatchWheel);
}

function useKeyboard({
  focus = undefined,
  canBeFocused = true,
  stopPropagation = false,
  keyDownDelay,
  keyDownHandler,
  keyUpHandler,
  keyPressHandler,
  pointerUpHandler,
}: IKeyboard) {
  useEffect(() => {
    const subscriber: Subscriber = {
      focus,
      canBeFocused,
      stopPropagation,
      keyDownDelay,
      keyDownHandler,
      keyUpHandler,
      keyPressHandler,
      pointerUpHandler,
      lastKeyDownTime: 0,
      lastKey: '',
      pressed: 0,
      lastFocus: -1,
      keyTimeout: undefined,
    };
    subscribers.push(subscriber);
    return () => {
      clearTimeout(subscriber.keyTimeout);
      const index = subscribers.indexOf(subscriber);
      if (index !== -1) subscribers.splice(index, 1);
    };
  }, []);
}

export default useKeyboard;
